import get from 'lodash/get';
import { INPUT_TYPES } from './constants';

const infixToPostfix = (expression) => {
  const output = [];
  const operatorStack = [];

  const precedence = {
    '+': 1,
    '-': 1,
    '*': 2,
    '/': 2,
  };

  const isOperator = (token) => '+-*/'.indexOf(token) !== -1;

  const tokens = expression.match(/\d+(\.\d+)?|[a-z]+|[+\-*\/()]/g);

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];

    if (/^\d+(\.\d+)?$/.test(token)) {
      output.push(token);
    } else if (token === '(') {
      operatorStack.push(token);
    } else if (token === ')') {
      while (operatorStack[operatorStack.length - 1] !== '(') {
        output.push(operatorStack.pop());
      }
      operatorStack.pop();
    } else if (isOperator(token)) {
      while (
        operatorStack.length > 0 &&
        isOperator(operatorStack[operatorStack.length - 1]) &&
        precedence[operatorStack[operatorStack.length - 1]] >= precedence[token]
      ) {
        output.push(operatorStack.pop());
      }
      operatorStack.push(token);
    }
  }

  while (operatorStack.length > 0) {
    output.push(operatorStack.pop());
  }

  return output.join(' ').trim();
};

const getResult = (token, operand1, operand2) => {
  let result;
  switch (token) {
    case '+':
      result = operand1 + operand2;
      break;
    case '-':
      result = operand1 - operand2;
      break;
    case '*':
      result = operand1 * operand2;
      break;
    case '/':
      if (operand2 === 0) {
        return '';
      }
      result = operand1 / operand2;
      break;
    default:
      return '';
  }

  return result;
};

export const evaluateFormula = (expression) => {
  const postFixExpression = infixToPostfix(expression);
  const postFixExpressionArray = postFixExpression.split(' ');
  const evaluationStack = [];
  const operators = ['+', '-', '*', '/'];

  for (const token of postFixExpressionArray) {
    if (operators.includes(token)) {
      if (evaluationStack.length < 2) {
        return '';
      }
      const b = Number(evaluationStack.pop());
      const a = Number(evaluationStack.pop());
      const result = getResult(token, a, b);
      if (!result) {
        return '';
      }
      evaluationStack.push(result);
    } else {
      evaluationStack.push(token);
    }
  }

  if (evaluationStack.length === 1) {
    return evaluationStack.pop();
  }

  return '';
};

const isAllowedChar = (char) => {
  const asciiValue = char.charCodeAt(0);
  const isNumber = asciiValue >= 48 && asciiValue <= 57;
  // ascii value of " " is 32
  // ascii value of "," is 46
  // ascii value of "/" is 47
  return isNumber || asciiValue === 46 || asciiValue === 32 || asciiValue === 47;
};

const isAllowedSuperScript = (char) => {
  const asciiValue = char.charCodeAt(0);

  // Ascii Values of "²" and "³" are 178 and 179
  return asciiValue === 178 || asciiValue === 179;
};

const evaluateFraction = (str) => {
  const parts = str.split('/');
  const numerator = parseInt(parts[0], 10);
  const denominator = parseInt(parts[1], 10);
  return numerator / denominator;
};

export const extractNumberFromValue = (input: string) => {
  // for brazil paint products we are getting ',' instead of '.'
  const inputWithoutSpaces = input.trim().replace(',', '.');
  const inputArray = inputWithoutSpaces.split('');
  const resultArray = [];
  let result = [];
  for (let i = 0; i < inputArray.length; i++) {
    const char = inputArray[i];
    const previousChar = inputArray[i - 1] || '';
    if (isAllowedChar(char) && (isAllowedChar(previousChar) || i === 0 || isAllowedSuperScript(previousChar))) {
      result.push(char);
    } else {
      if (result.length > 0) {
        const valStr = result.join('');
        let parsedValue: string | number = valStr;
        if (valStr.includes('/')) {
          parsedValue = evaluateFraction(parsedValue);
        }
        resultArray.push(Number(parsedValue));
      }
      result = [];
    }

    if (i === inputArray.length - 1 && result.length > 0) {
      const valStr = result.join('');
      let parsedValue: string | number = valStr;
      if (valStr.includes('/')) {
        parsedValue = evaluateFraction(parsedValue);
      }
      resultArray.push(Number(parsedValue));
    }
  }

  return resultArray[0];
};

export const createValuesMapFromInputs = (inputsRequiredForCal) => {
  const valuesMap = {};
  for (const calInput of inputsRequiredForCal) {
    const value = get(calInput, 'value', '').toString();
    const parsedvalue = value.includes(',') ? value.replace(/,/g, '.') : value;
    valuesMap[calInput.short_name] = Number(parsedvalue);
  }

  return valuesMap;
};

export const substituteValuesInFormula = (formula, valuesMap) => {
  let formulaWithSubstitutedValues = formula;
  for (const key in valuesMap) {
    formulaWithSubstitutedValues = formulaWithSubstitutedValues.replace(new RegExp(key, 'g'), valuesMap[key]);
  }

  return formulaWithSubstitutedValues;
};

export const removeSpacesAndConvertToLowerCase = (str) => {
  return str.replace(/\s/g, '').toLowerCase();
};

export const getAttributeValue = (attributeTitle, allAttributes) => {
  const filteredAttributes = allAttributes.filter((attributeData) => {
    const configuredAttribute = removeSpacesAndConvertToLowerCase(attributeTitle);
    const availableAttribute = removeSpacesAndConvertToLowerCase(get(attributeData, 'name', ''));
    return configuredAttribute === availableAttribute;
  });

  if (filteredAttributes && filteredAttributes.length > 0) {
    const value = get(filteredAttributes[0], 'values[0]', '');
    return value;
  }

  return '';
};

export const getRequiredAttributesForCal = (configuredAttributes, allAttributes) => {
  return configuredAttributes
    .map((attributeData) => {
      const { title, short_name } = attributeData;
      const attributeValue = getAttributeValue(title, allAttributes);
      const extractedAttributeValue = extractNumberFromValue(attributeValue);
      return {
        title,
        value: extractedAttributeValue,
        short_name,
      };
    })
    .filter((attributeData) => !!attributeData.value);
};

export const parseAttributes = (attributes) => {
  return attributes.map((attribute) => {
    const currentValue = get(attribute, 'value', '');
    const attributeName = get(attribute, 'name', '');
    let parsedValue;
    if (Array.isArray(currentValue)) {
      parsedValue = currentValue;
    } else {
      parsedValue = [currentValue];
    }
    return { name: attributeName, values: parsedValue };
  });
};

export const isNotPaintCalculator = (uiInputs) => {
  const areTwoInputs = uiInputs.length === 2;
  let isNumberInputExists = false;
  let isCounterInputExists = false;

  uiInputs.forEach((inputData) => {
    const type = get(inputData, 'input_type', '');
    if (Object.keys(INPUT_TYPES).includes(type)) {
      isNumberInputExists = true;
    }
    if (type === 'Counter') {
      isCounterInputExists = true;
    }
  });

  return !(areTwoInputs && isCounterInputExists && isNumberInputExists);
};
